import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import BidStrategyConfiguration from './components/BidStrategyConfiguration';
import PerformanceMonitoringPanel from './components/PerformanceMonitoringPanel';
import BulkBidManagement from './components/BulkBidManagement';
import RealTimeBidActivity from './components/RealTimeBidActivity';
import AIAnalysisPanel from './components/AIAnalysisPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const BuyerBidManagementInterface = () => {
  const navigate = useNavigate();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSystemStatusOpen, setIsSystemStatusOpen] = useState(false);
  const [activeView, setActiveView] = useState('strategy');
  const [selectedItems, setSelectedItems] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event?.ctrlKey || event?.metaKey) {
        switch (event?.key?.toLowerCase()) {
          case 's':
            event?.preventDefault();
            handleSaveStrategy();
            break;
          case 'r':
            event?.preventDefault();
            window.location?.reload();
            break;
          default:
            break;
        }
      }
      
      if (event?.key === 'F5') {
        event?.preventDefault();
        window.location?.reload();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSaveStrategy = (strategy = null) => {
    setLastSaved(new Date());
    console.log('Strategy saved:', strategy);
    // Simulate save success notification
    setTimeout(() => {
      // This would typically trigger a notification
    }, 500);
  };

  const handleBulkUpdate = (updateData) => {
    console.log('Bulk update applied:', updateData);
    setSelectedItems([]);
  };

  const handleTemplateApply = (templateId) => {
    console.log('Template applied:', templateId);
  };

  const handleTemplateCreate = (profileIds) => {
    console.log('Template created from profiles:', profileIds);
  };

  const handleExportData = () => {
    console.log('Exporting performance data...');
    // Simulate export
    const data = {
      timestamp: new Date(),
      type: 'performance_report',
      format: 'csv'
    };
    console.log('Export completed:', data);
  };

  const handleBidAction = (action, data) => {
    console.log('Bid action:', action, data);
  };

  const handleQuickAction = (actionId, items) => {
    console.log('Quick action:', actionId, items);
    switch (actionId) {
      case 'bulk_bid': setActiveView('bulk');
        break;
      case 'export_leads':
        handleExportData();
        break;
      case 'set_auto_bid': setActiveView('strategy');
        break;
      default:
        break;
    }
  };

  const viewOptions = [
    { key: 'strategy', label: 'Strategy Configuration', icon: 'Settings' },
    { key: 'performance', label: 'Performance Monitor', icon: 'BarChart3' },
    { key: 'ai-analysis', label: 'AI Analysis', icon: 'Brain' },
    { key: 'bulk', label: 'Bulk Management', icon: 'Layers' },
    { key: 'activity', label: 'Live Activity', icon: 'Zap' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole="buyer"
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={() => setIsNotificationOpen(!isNotificationOpen)}
      />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="px-4 lg:px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Bid Management Interface</h1>
                <p className="text-muted-foreground mt-1">
                  Configure automated bidding strategies and monitor lead acquisition performance
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {lastSaved && (
                  <div className="flex items-center space-x-2 text-sm text-success">
                    <Icon name="Check" size={16} />
                    <span>Saved {lastSaved?.toLocaleTimeString()}</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  iconName="HelpCircle"
                  onClick={() => navigate('/help/bid-management')}
                >
                  Help
                </Button>
              </div>
            </div>

            {/* View Navigation */}
            <div className="flex items-center space-x-1 bg-muted/30 p-1 rounded-lg">
              {viewOptions?.map((view) => (
                <button
                  key={view?.key}
                  onClick={() => setActiveView(view?.key)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeView === view?.key
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  <span className="hidden sm:inline">{view?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 lg:px-6 py-4">
          <QuickActionToolbar
            section="buyer"
            selectedItems={selectedItems}
            onAction={handleQuickAction}
          />
        </div>

        {/* Main Content */}
        <div className="px-4 lg:px-6 pb-8">
          {activeView === 'strategy' && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2">
                <BidStrategyConfiguration
                  onSaveStrategy={handleSaveStrategy}
                  onTemplateApply={handleTemplateApply}
                />
              </div>
              <div className="lg:col-span-3">
                <PerformanceMonitoringPanel
                  onExportData={handleExportData}
                />
              </div>
            </div>
          )}

          {activeView === 'performance' && (
            <div className="space-y-6">
              <PerformanceMonitoringPanel
                onExportData={handleExportData}
              />
            </div>
          )}

          {activeView === 'ai-analysis' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <AIAnalysisPanel performanceData={{}} />
              </div>
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="TrendingUp" size={16} className="mr-2 text-accent" />
                    KPI Summary
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Current CPA</span>
                      <span className="font-medium text-foreground">$42.75</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Avg Call Length</span>
                      <span className="font-medium text-foreground">4.2 min</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Win Rate</span>
                      <span className="font-medium text-accent">33.9%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Quality Score</span>
                      <span className="font-medium text-success">8.7/10</span>
                    </div>
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Lightbulb" size={16} className="mr-2 text-warning" />
                    AI Tips
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={14} className="mt-0.5 text-accent" />
                      <span>California shows 15% higher win rates - consider increasing bids</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={14} className="mt-0.5 text-accent" />
                      <span>Calls over 4 minutes convert 23% better</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={14} className="mt-0.5 text-accent" />
                      <span>Auto insurance leads perform best between 2-4 PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'bulk' && (
            <div className="space-y-6">
              <BulkBidManagement
                onBulkUpdate={handleBulkUpdate}
                onTemplateCreate={handleTemplateCreate}
              />
            </div>
          )}

          {activeView === 'activity' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RealTimeBidActivity
                  onBidAction={handleBidAction}
                />
              </div>
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4">Quick Stats</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Active Bids</span>
                      <span className="font-medium text-foreground">3</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Budget Remaining</span>
                      <span className="font-medium text-success">$1,247</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Today's Wins</span>
                      <span className="font-medium text-foreground">12</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Avg. Win Rate</span>
                      <span className="font-medium text-accent">34.2%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Overlays */}
      <NotificationCenter
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
      <SystemStatusIndicator
        isExpanded={isSystemStatusOpen}
        onToggle={() => setIsSystemStatusOpen(!isSystemStatusOpen)}
      />
    </div>
  );
};

export default BuyerBidManagementInterface;